import React from 'react';
import styled from 'styled-components';

import { listElementHoverCss } from '@ui/hoverStates.ts';
import { Icon } from '@ui/icons/Icon.tsx';
import PoiStatusPill, { PoiOpenStatusText } from '@ui/labels/PoiStatusPill.tsx';
import { SecurityLabel } from '@ui/labels/SecurityLabel.tsx';
import { getPoiIconId } from '@utils/poi.ts';

import { handleKeyboardEscape, handleKeyboardSelect } from '../../../plugins/utils/keyboardUtils.ts';
import { ParkingLabelStatus } from '../../../utils/constants.ts';
import type { PoiOpenStatusResult, SearchPoi, TranslateFn } from './types.ts';
import { getCategoryLabel } from './utils.ts';

const SearchResultsElem = styled.button`
  display: flex;
  flex-direction: column;
  padding: 8px 14px 12px;
  position: relative;
  margin: 0;
  cursor: pointer;
  background: none;
  width: 100%;
  border: none;
  text-align: start;

  &:hover {
    ${listElementHoverCss};
  }

  &:not(:last-child):after {
    content: '';
    position: absolute;
    width: calc(100% - 32px);
    margin: auto;
    bottom: 0;
    height: 1px;
    background-color: #ebebeb;
  }
  &:focus-visible {
    outline-offset: -3px;
    border-radius: 4px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const IconWrapper = styled.div`
  width: 36px;
  text-align: center;

  ${Icon} {
    vertical-align: middle;
  }
`;

const RoomInfo = styled.div`
  margin-inline-start: 12px;
  margin-inline-end: 12px;

  span {
    display: block;
  }

  .label {
    display: block;
    color: var(--color-primaryText);
    ${({ theme }) => theme.fontSize('16px')};
    font-weight: 500;
    line-height: 150%;
  }

  .label--light {
    font-weight: 500;
  }

  .info {
    color: var(--color-secondaryText);
    ${({ theme }) => theme.fontSize('14px')};
    font-weight: 400;
    line-height: 150%;
  }
`;

const SecurityWaitLabelStyled = styled(SecurityLabel)`
  margin-top: 6px;
  margin-inline-start: 49px;
`;

const PoiStatusPillStyled = styled(PoiStatusPill)`
  margin-top: 6px;
  margin-inline-start: 49px;
`;

const PoiOpenStatusTextStyled = styled(PoiOpenStatusText)`
  margin-top: 6px;
  margin-inline-start: 49px;
`;

type SearchResultsListProps = {
  className?: string;
  handleEscapeKey: () => void;
  /** Open/closed state per POI id, resolved on demand by the plugin layer. */
  openStatuses?: Record<string, PoiOpenStatusResult>;
  isDefault?: boolean;
  isDesktop?: boolean;
  onChooseMapLocationClicked?: () => void;
  onResultClicked: (result: SearchPoi) => void;
  results: SearchPoi[];
  T: TranslateFn;
};

type SearchResultProps = {
  result: SearchPoi;
  T: TranslateFn;
  onResultClicked: (result: SearchPoi) => void;
  handleEscapeKey: () => void;
  openStatus?: PoiOpenStatusResult;
};

const getLocationInfo = (poi: SearchPoi, T: TranslateFn) => {
  if (poi.roomId) {
    return [poi.roomId, poi.position.floorName];
  } else {
    const nearbyLandmark = poi.nearbyLandmark && T('ui:Near _location_', { location: poi.nearbyLandmark });
    return [nearbyLandmark, poi.position.floorName, poi.position.structureName];
  }
};
const buildDetails = (poi: SearchPoi, T: TranslateFn) => getLocationInfo(poi, T).filter(Boolean).join(' • ');

const SearchResult = ({ result, T, onResultClicked, handleEscapeKey, openStatus }: SearchResultProps) => {
  const dynamicData = result.dynamicData?.security;

  const statusText = dynamicData?.isTemporarilyClosed
    ? T('ui:Closed')
    : T('ui:_xx_ minute wait', { count: dynamicData?.queueTime });

  const parkingDD = result.dynamicData?.parking;
  const openCloseDD = result.dynamicData?.['open-closed-status'];

  let poiStatus: string | undefined;

  if (parkingDD) {
    if (typeof parkingDD.lotStatus !== 'undefined' && parkingDD.lotStatus !== 'Unknown') {
      poiStatus = parkingDD.lotStatus;
    }
  } else if (openCloseDD && !openStatus) {
    // Legacy fallback: while the new open-status feature is off, keep rendering the existing
    // dynamic-data pill so venues relying on it do not silently lose the indicator.
    poiStatus = openCloseDD.isOpen ? ParkingLabelStatus.OPEN : ParkingLabelStatus.CLOSED;
  }

  // Already-translated label when the plugin supplied one, otherwise a plain Open/Closed.
  const openStatusLabel = openStatus
    ? (openStatus.timeLabel ?? (openStatus.isOpen ? T('ui:Open') : T('ui:Closed')))
    : null;

  const ariaLabel = React.useMemo(() => {
    const categoryLabel = getCategoryLabel(result.category, T);
    const combinedInfo = [buildDetails(result, T), poiStatus, openStatusLabel, dynamicData ? statusText : null]
      .filter(Boolean)
      .join(' ');

    // Build aria-label: "Category: Name, info: details" or "Name, info: details" if no category label
    const detailsSuffix = combinedInfo ? `, ${T('ui:info')}: ${combinedInfo}` : '';

    return categoryLabel
      ? `${categoryLabel}: ${result.name}${detailsSuffix}`.trim()
      : `${result.name}${detailsSuffix}`.trim();
  }, [result.category, result.name, result, poiStatus, openStatusLabel, T, dynamicData, statusText]);

  return (
    <SearchResultsElem
      data-testid={'searchResultsElementPoi'}
      tabIndex={0}
      onKeyDown={
        e => handleKeyboardSelect(() => onResultClicked(result))(e) || handleKeyboardEscape(handleEscapeKey)(e) // try one then the other..
      }
      aria-label={ariaLabel}
      onClick={() => onResultClicked(result)}
    >
      <ContentWrapper>
        <IconWrapper>
          <Icon name={getPoiIconId(result)} cdn width={36} height={36} aria-hidden="true" />
        </IconWrapper>
        <RoomInfo>
          <span className="label">{result.name}</span>
          <span className="info">{buildDetails(result, T)}</span>
        </RoomInfo>
      </ContentWrapper>
      <SecurityWaitLabelStyled poi={result} T={T} statusText={statusText} />
      {poiStatus && <PoiStatusPillStyled status={poiStatus} T={T} />}
      {openStatus && <PoiOpenStatusTextStyled status={openStatus.status} timeLabel={openStatus.timeLabel} T={T} />}
    </SearchResultsElem>
  );
};

const SearchResultsList = ({
  isDefault,
  isDesktop,
  openStatuses,
  results,
  onResultClicked,
  onChooseMapLocationClicked,
  handleEscapeKey,
  T,
}: SearchResultsListProps) => {
  return (
    <>
      {isDefault && !isDesktop && (
        <SearchResultsElem
          data-testid="chooseOnMap"
          onClick={onChooseMapLocationClicked}
          onKeyDown={handleKeyboardSelect(() => onChooseMapLocationClicked?.())}
          tabIndex={0}
          aria-label={T('ui:Choose location on map')}
        >
          <ContentWrapper>
            <IconWrapper>
              <Icon name="location" cdn width={32} height={32} fillColor="#666" aria-hidden="true" />
            </IconWrapper>
            <RoomInfo>
              <span className="label label--light">{T('ui:Choose location on map')}</span>
            </RoomInfo>
          </ContentWrapper>
        </SearchResultsElem>
      )}
      {results.map((result, index) => (
        <SearchResult
          key={index}
          result={result}
          T={T}
          openStatus={result.poiId != null ? openStatuses?.[result.poiId] : undefined}
          onResultClicked={onResultClicked}
          handleEscapeKey={handleEscapeKey}
        />
      ))}
    </>
  );
};

export default SearchResultsList;
